import React from 'react';
import ReactDOM from 'react-dom';
import { BoldSmallBodyText, Button, FlexRow, IconSize, IconType, NumberInput } from 'ui-toolkit';
import CostDataModal from '../../CostDataModal';
import Currency from '../../Currency';
import { RotateIcon } from '../../RotateIcon.styled';
import IItemProps from '../IItemProps';
import SharedItemCells, { SharedItemCellsActiveInput } from '../SharedItemCells';
import {
  StyledDeedMortgageRecordingFees,
  StyledDeedMortgageRecordingFeesCell,
  StyledDeedMortgageRecordingFeesContainer,
  StyledDeedMortgageRecordingFeesCostDataCell,
} from './DeedMortgageRecordingFees.styles';
import { ICDV3ViewItem, IRecordingFeeViewItem } from '../../../../ui-toolkit/cdItems/interfaces/ICDViewItem';
import { generateDefaultViewItem } from '../../../../ui-toolkit/cdItems/data/generateDefaultViewItem';
import { eRecordingFees } from '../../../../ui-toolkit/enums/eRecordingFees';
import { eCDV3ItemTypes } from '../../../../ui-toolkit/enums/eCDV3ItemTypes';
import { eLenderPaid } from '../../../../ui-toolkit/enums/eLenderPaid';
import { convertToFloat } from '../../../../ui-toolkit/utilities/convertToFloat';
import { sum } from '../../../../ui-toolkit/utilities/sum';

interface IDeedMortgageRecordingFeesProps extends IItemProps {
  data: IRecordingFeeViewItem[];
  onChange: (item: ICDV3ViewItem) => void;
}

interface IDeedMortgageRecordingFeesState {
  expanded: boolean;
  sharedItemCellsActiveInput: SharedItemCellsActiveInput | null;
}

function generateAmountDisplayData(
  items: IRecordingFeeViewItem[],
  selector: (item: IRecordingFeeViewItem) => number | null | undefined
) {
  const amounts = items.map(selector);
  return {
    isEmpty: amounts.every((amount) => amount === undefined || amount === null),
    sum: amounts.map(convertToFloat).reduce(sum, 0),
  };
}

function generateItemTotal(item: IRecordingFeeViewItem) {
  return (item.buyer_at_closing === null || item.buyer_at_closing === undefined) &&
    (item.buyer_before_closing === null || item.buyer_before_closing === undefined) &&
    (item.seller_at_closing === null || item.seller_at_closing === undefined) &&
    (item.seller_before_closing === null || item.seller_before_closing === undefined) &&
    (item.paid_by_others === null || item.paid_by_others === undefined)
    ? null
    : convertToFloat(item.buyer_at_closing) +
        convertToFloat(item.buyer_before_closing) +
        convertToFloat(item.seller_at_closing) +
        convertToFloat(item.seller_before_closing) +
        convertToFloat(item.paid_by_others);
}

export class DeedMortgageRecordingFees extends React.Component<
  IDeedMortgageRecordingFeesProps,
  IDeedMortgageRecordingFeesState
> {
  private cardModal = React.createRef<CostDataModal>();

  constructor(props: IDeedMortgageRecordingFeesProps) {
    super(props);
    this.state = { expanded: false, sharedItemCellsActiveInput: null };
    this.onBlurEvent = this.onBlurEvent.bind(this);
    this.onMouseDownEvent = this.onMouseDownEvent.bind(this);
    this.onExpandEvent = this.onExpandEvent.bind(this);
    this.onSharedItemsCellsChange = this.onSharedItemsCellsChange.bind(this);
    this.onCostDataCellChange = this.onCostDataCellChange.bind(this);
  }

  public componentDidMount() {
    document.addEventListener('mousedown', this.onMouseDownEvent);
  }

  public componentWillMount() {
    document.removeEventListener('mousedown', this.onMouseDownEvent);
  }

  public render() {
    const deeds = this.props.data.filter((item) => item.sub_type === eRecordingFees.deed);
    const mortgages = this.props.data.filter((item) => !deeds.includes(item));
    const { sharedItemCellsActiveInput, expanded } = this.state;
    const deedBuyerAtClosing = generateAmountDisplayData(deeds, (deed) => deed.buyer_at_closing);
    const deedBuyerBeforeClosing = generateAmountDisplayData(deeds, (deed) => deed.buyer_before_closing);
    const deedSellerAtClosing = generateAmountDisplayData(deeds, (deed) => deed.seller_at_closing);
    const deedSellerBeforeClosing = generateAmountDisplayData(deeds, (deed) => deed.seller_before_closing);
    const deedPaidByOthers = generateAmountDisplayData(deeds, (deed) => deed.paid_by_others);
    const mortgageBuyerAtClosing = generateAmountDisplayData(mortgages, (mortgage) => mortgage.buyer_at_closing);
    const mortgageBuyerBeforeClosing = generateAmountDisplayData(
      mortgages,
      (mortgage) => mortgage.buyer_before_closing
    );
    const mortgageSellerAtClosing = generateAmountDisplayData(mortgages, (mortgage) => mortgage.seller_at_closing);
    const mortgageSellerBeforeClosing = generateAmountDisplayData(
      mortgages,
      (mortgage) => mortgage.seller_before_closing
    );
    const mortgagePaidByOthers = generateAmountDisplayData(mortgages, (mortgage) => mortgage.paid_by_others);

    const deedTotal =
      deedBuyerAtClosing.sum +
      deedBuyerBeforeClosing.sum +
      deedSellerAtClosing.sum +
      deedSellerBeforeClosing.sum +
      deedPaidByOthers.sum;
    const mortgageTotal =
      mortgageBuyerAtClosing.sum +
      mortgageBuyerBeforeClosing.sum +
      mortgageSellerAtClosing.sum +
      mortgageSellerBeforeClosing.sum +
      mortgagePaidByOthers.sum;
    const combinedRecordingFee = {
      ...generateDefaultViewItem(),
      buyer_at_closing:
        !deedBuyerAtClosing.isEmpty || !mortgageBuyerAtClosing.isEmpty
          ? deedBuyerAtClosing.sum + mortgageBuyerAtClosing.sum
          : null,
      buyer_before_closing:
        !deedBuyerBeforeClosing.isEmpty || !mortgageBuyerBeforeClosing.isEmpty
          ? deedBuyerBeforeClosing.sum + mortgageBuyerBeforeClosing.sum
          : null,
      netted_out: this.props.data.every((item) => !!item.netted_out),
      lender_paid: this.combinedLenderPaid(),
      paid_by_others:
        !deedPaidByOthers.isEmpty || !mortgagePaidByOthers.isEmpty
          ? deedPaidByOthers.sum + mortgagePaidByOthers.sum
          : null,
      seller_at_closing:
        !deedSellerAtClosing.isEmpty || !mortgageSellerAtClosing.isEmpty
          ? deedSellerAtClosing.sum + mortgageSellerAtClosing.sum
          : null,
      seller_before_closing:
        !deedSellerBeforeClosing.isEmpty || !mortgageBuyerBeforeClosing.isEmpty
          ? deedSellerBeforeClosing.sum + mortgageBuyerBeforeClosing.sum
          : null,
      type: eCDV3ItemTypes.recording_fee,
      overridden: false,
    } as IRecordingFeeViewItem;
    return (
      <StyledDeedMortgageRecordingFeesContainer>
        <SharedItemCells
          {...this.props}
          data={combinedRecordingFee}
          showRemove={false}
          inputDisplayOptions={{ disabled: true }}
          onChange={this.onSharedItemsCellsChange}
          readOnly={!this.props.hasEditPermissions}
        >
          <StyledDeedMortgageRecordingFeesCell onClick={this.onExpandEvent}>
            <div>Recording Fees</div>
            <FlexRow width={'max-content'}>
              <div>
                <div>Deed</div>
                <div>{deedTotal ? <Currency value={deedTotal} /> : '$__.__'}</div>
              </div>
              <div>
                <div>Mortgage</div>
                <div>{mortgageTotal ? <Currency value={mortgageTotal} /> : '$__.__'}</div>
              </div>
              <Button intent="ghost" isInline>
                <RotateIcon type={IconType.CaretDd} isRotated={false} size={IconSize.Small} />
              </Button>
            </FlexRow>
          </StyledDeedMortgageRecordingFeesCell>
          {expanded ? (
            <StyledDeedMortgageRecordingFees modal={true}>
              <CostDataModal
                className="cost-data--deed-mortgage"
                ref={this.cardModal}
                onToggle={this.onExpandEvent}
                title={<BoldSmallBodyText>Recording Fees</BoldSmallBodyText>}
              >
                {this.props.data.map((item, index) => (
                  <SharedItemCells
                    {...this.props}
                    key={item.id}
                    activeInput={index === 0 ? sharedItemCellsActiveInput : null}
                    data={item}
                    onBlur={this.onBlurEvent}
                    onChange={this.onCostDataCellChange}
                    showLenderPaid={false}
                    showRemove={false}
                    rowNumberDisplay="none"
                    nettedOutDisplay="none"
                    readOnly={!this.props.hasEditPermissions}
                  >
                    <FlexRow width={100} justifyContent={'space-between'}>
                      <div>{item.sub_type === eRecordingFees.deed ? 'Deed' : 'Mortgage'}</div>
                      <StyledDeedMortgageRecordingFeesCostDataCell>
                        <NumberInput readOnly={true} disabled={true} mode="currency" value={generateItemTotal(item)} />
                      </StyledDeedMortgageRecordingFeesCostDataCell>
                    </FlexRow>
                  </SharedItemCells>
                ))}
              </CostDataModal>
            </StyledDeedMortgageRecordingFees>
          ) : null}
        </SharedItemCells>
      </StyledDeedMortgageRecordingFeesContainer>
    );
  }

  protected onBlurEvent(event: React.FocusEvent) {
    const node = this.cardModal.current;
    const domNode = ReactDOM.findDOMNode(node);
    if (domNode && event.relatedTarget && !domNode.contains(event.relatedTarget as HTMLElement)) {
      this.setState({
        expanded: false,
        sharedItemCellsActiveInput: null,
      });
    }
  }

  protected onMouseDownEvent(event: MouseEvent) {
    const node = this.cardModal.current;
    const domNode = ReactDOM.findDOMNode(node);
    if (domNode && !domNode.contains(event.target as HTMLElement)) {
      this.setState({
        expanded: false,
        sharedItemCellsActiveInput: null,
      });
    }
  }

  protected onExpandEvent() {
    this.setState((state) => ({
      expanded: !state.expanded,
      sharedItemCellsActiveInput: SharedItemCellsActiveInput.BuyerAtClosing,
    }));
  }

  private combinedLenderPaid(): eLenderPaid {
    return this.props.data.reduce<eLenderPaid>(
      (accum, value) => (accum === eLenderPaid.OtherNotSending && value.lender_paid ? value.lender_paid : accum),
      eLenderPaid.OtherNotSending
    );
  }

  // This runs when netted_out or lender_paid changes
  private onSharedItemsCellsChange(sharedItem: ICDV3ViewItem) {
    const { onChange } = this.props;
    const netted_out = sharedItem.netted_out;
    const lender_paid = sharedItem.lender_paid;
    this.props.data.forEach((item) => {
      if (item.netted_out !== netted_out) {
        onChange({ ...generateDefaultViewItem(), ...item, netted_out, dirty: true });
      }
      if (item.lender_paid !== lender_paid) {
        onChange({ ...generateDefaultViewItem(), ...item, lender_paid, dirty: true });
      }
    });
  }

  // This runs when either deed or mortgage fee cost cells are changed
  private onCostDataCellChange(cdItem: ICDV3ViewItem) {
    const { id, buyer_at_closing, buyer_before_closing, seller_at_closing, seller_before_closing, paid_by_others } =
      cdItem;
    const updatedItems = this.props.data.map((item) =>
      item.id === id
        ? { ...item, buyer_at_closing, buyer_before_closing, seller_at_closing, seller_before_closing, paid_by_others }
        : item
    );
    const hasPaidByOthers = updatedItems.some((item) => item.paid_by_others);

    // If either item has a non-null paid_by_others value, they should all have the same lender_paid value,
    // with preference for anything besides the default OtherNotSending
    const lender_paid: eLenderPaid = hasPaidByOthers ? this.combinedLenderPaid() : eLenderPaid.OtherNotSending;
    const { onChange } = this.props;
    this.props.data.forEach((item) => {
      if (item.id === id)
        onChange({
          ...item,
          buyer_at_closing,
          buyer_before_closing,
          seller_at_closing,
          seller_before_closing,
          paid_by_others,
          lender_paid,
          dirty: true,
        });
      else if (item.lender_paid !== lender_paid) onChange({ ...item, lender_paid, dirty: true });
    });
  }
}
